import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Candidate } from '../../../types';
import Button from '../../Form/Button';
import Select from '../../Form/Select';
import TextArea from '../../Form/TextArea';
import Input from '../../Form/Input';
import { useCandidateRatings } from '../../../hooks/useCandidateRatings';

interface QuickUpdateForm {
  status: string;
  managerFeedback: string;
  interviewDate: string;
  notes: string;
}

interface CandidateEvaluationTabProps {
  candidate: Candidate;
  onUpdate: (candidate: Candidate) => void;
  statusOptions: { value: string; label: string }[];
}

const CandidateEvaluationTab: React.FC<CandidateEvaluationTabProps> = ({
  candidate,
  onUpdate,
  statusOptions
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // `rating` is absent on candidates that were never rated — default to 0
  // instead of crashing the whole page (Cannot read properties of undefined).
  const ratingCount = candidate.rating?.length || 0;

  // The Candidates Evaluation module stores per-point rows on
  // candidate-rating — surface this candidate's latest evaluation batch here
  // so the tab reflects the same data instead of the legacy `rating` array.
  const ratingsQuery = useMemo(() => ({ populate: '*', pagination: { pageSize: 500 } }), []);
  const { data: candidateRatings = [] } = useCandidateRatings(ratingsQuery);
  const candidateKey = String((candidate as any).documentId || candidate.id);
  const latestEval = useMemo(() => {
    const rows = (candidateRatings as any[]).filter((r) => {
      const c = r.candidateData || r.candidate;
      const cid = c && typeof c === 'object' ? String(c.documentId || c.id || '') : String(c ?? '');
      return cid === candidateKey;
    });
    if (!rows.length) return null;
    const batches = new Map<string, any[]>();
    rows.forEach((r) => { const k = r.evaluatedAt || ''; batches.set(k, [...(batches.get(k) || []), r]); });
    const latest = Array.from(batches.entries()).sort(([a], [b]) => b.localeCompare(a))[0];
    const batchRows = latest[1];
    const total = batchRows.reduce((s: number, r: any) => s + (r.score || 0), 0);
    const max = batchRows.length * 4;
    return { date: latest[0], evaluator: batchRows[0].evaluatedBy, total, max, pct: max > 0 ? (total / max) * 100 : 0 };
  }, [candidateRatings, candidateKey]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<QuickUpdateForm>({
    defaultValues: {
      status: candidate.status,
      managerFeedback: candidate.managerFeedback,
      interviewDate: candidate.interviewDate ? candidate.interviewDate.split('T')[0] : '',
      notes: candidate.notes
    }
  });

  const handleQuickUpdate = (data: QuickUpdateForm) => {
    const updatedCandidate: Candidate = {
      ...candidate,
      status: data.status as any,
      managerFeedback: data.managerFeedback,
      interviewDate: data.interviewDate ? new Date(data.interviewDate).toISOString() : candidate.interviewDate,
      notes: data.notes
    };
    onUpdate(updatedCandidate);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Current Evaluation</h4>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Assigned Manager</dt>
              <dd className="text-sm text-gray-900">{candidate.assignedManagerName || 'Not assigned'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Assessment Rating</dt>
              <dd className="flex items-center">
                {latestEval ? (
                  <div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-800">{latestEval.pct.toFixed(0)}%</span>
                      </div>
                      <span className="ml-2 text-xs text-gray-500">{latestEval.total}/{latestEval.max} pts</span>
                      <span className="ml-2 text-xs text-gray-400">by {latestEval.evaluator}</span>
                    </div>
                    <div className="mt-1 text-xs text-gray-400">
                      {latestEval.date ? new Date(latestEval.date).toLocaleDateString() : ''}
                    </div>
                  </div>
                ) : ratingCount > 0 ? (
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-800">{candidate.totalRating || 0}</span>
                    </div>
                    <span className="ml-2 text-xs text-gray-500">/10</span>
                    <span className="ml-2 text-xs text-gray-400">
                      ({ratingCount} point{ratingCount !== 1 ? 's' : ''})
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">Not rated yet</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Interview Date</dt>
              <dd className="text-sm text-gray-900">
                {candidate.interviewDate ? new Date(candidate.interviewDate).toLocaleDateString() : 'Not scheduled'}
              </dd>
            </div>
          </dl>
        </div>

        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Timeline</h4>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Applied</dt>
              <dd className="text-sm text-gray-900">{new Date(candidate.appliedDate).toLocaleDateString()}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="text-sm text-gray-900">{new Date(candidate.updatedAt).toLocaleDateString()}</dd>
            </div>
          </dl>
        </div>
      </div>

      {candidate.managerFeedback && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Manager Feedback</h4>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{candidate.managerFeedback}</p>
          </div>
        </div>
      )}

      {candidate.notes && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Notes</h4>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{candidate.notes}</p>
          </div>
        </div>
      )}

      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Quick Update</h4>
          <Button
            type="button"
            variant={isEditing ? 'secondary' : 'primary'}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit Evaluation'}
          </Button>
        </div>

        {isEditing && (
          <form onSubmit={handleSubmit(handleQuickUpdate)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Status"
                {...register('status')}
                options={statusOptions}
              />

              <Input
                label="Interview Date"
                type="date"
                {...register('interviewDate')}
              />
            </div>

            <TextArea
              label="Manager Feedback"
              {...register('managerFeedback')}
              placeholder="Add your feedback and evaluation notes..."
              rows={3}
            />

            <TextArea
              label="Additional Notes"
              {...register('notes')}
              placeholder="Any additional observations or notes..."
              rows={2}
            />

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Evaluation'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CandidateEvaluationTab;
